import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { TIER_LABELS } from '../../utils/tiers';
import { formatCentsAsDollars } from '../../utils/money';
import { formatNumber } from '../admin/shared';
import { TableCard, TableHeader, TableViewport, thClass, tdClass } from '../table/TableKit';

function BillingTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['billing', apiService.getInstanceInfo()?.instanceKey],
    queryFn: apiService.getBilling,
    retry: false,
  });

  if (isLoading) return <div role="status" className="text-sm text-secondary p-6">Loading billing...</div>;
  if (error || !data) {
    return <div role="alert" className="mb-4 bg-red-600/20 text-red-400 px-4 py-2 rounded text-sm">Failed to load billing.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-widget-bg rounded-lg border border-gray-700 p-6">
        <dl className="flex flex-wrap gap-x-10 gap-y-3">
          <div className="flex gap-2">
            <dt className="text-secondary">License tier</dt>
            <dd className="text-heading">{TIER_LABELS[data.tier]}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-secondary">Credit balance</dt>
            <dd className="text-heading">{formatNumber(data.credits.balance)} tokens</dd>
          </div>
        </dl>
      </div>
      {data.months.length === 0 ? (
        <p className="text-sm text-secondary">No billed months yet. A month appears here after it ends.</p>
      ) : (
        <TableCard>
          <TableHeader title="Monthly billing" />
          <TableViewport>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  {['Month', 'Usage', 'Credits', 'Amount due'].map((label) => <th key={label} className={thClass}>{label}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {data.months.map((month) => (
                  <tr key={month.month}>
                    <td className={tdClass}>{new Date(`${month.month}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}</td>
                    <td className={tdClass}>
                      {formatCentsAsDollars(month.cost_cents)}
                      <span className="block text-xs text-secondary">{formatNumber(month.input_tokens_converted)} tokens</span>
                    </td>
                    <td className={tdClass}>
                      {month.credit_cents > 0 && '−'}{formatCentsAsDollars(month.credit_cents)}
                      <span className="block text-xs text-secondary">{formatNumber(month.credit_tokens_applied)} tokens</span>
                    </td>
                    <td className={tdClass}>{formatCentsAsDollars(month.billed_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableViewport>
        </TableCard>
      )}
    </div>
  );
}

export default BillingTab;
